import { useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { localAuthRepository } from "../repository/localAuthRepository";
import { refreshStaffAccessToken } from "../infrastructure/httpDao";
import {
  type ChatMessage,
  type ChatMessageReaction,
  type ChatRoomActivity,
  type ChatRoomReadEvent,
  type ChatRoomWorkflowActivity,
} from "../types/chat";
import { getPublicSocketBaseUrl } from "@/lib/apiBaseUrl";

type ChatSocketError = {
  code: string;
  roomId?: string;
  message: string;
};

type JoinedRoomEvent = {
  roomId: string;
};

export type StaffInboxChangedEvent = {
  reason: "event-recorded" | "read-state-changed";
  occurredAt: string;
};

const toBearerToken = (token: string) => `Bearer ${token}`;

const emitJoinRoom = (socket: Socket, roomId: string) => {
  socket.emit("joinRoom", { roomId });
};

export const useChatSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const joinedRoomIdsRef = useRef(new Set<string>());
  const roomAccessRecoveryAttemptsRef = useRef(new Set<string>());
  const handshakeTokenRef = useRef<string | null>(null);
  const authRecoveryRef = useRef<Promise<void> | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const reconnectWithFreshToken = useCallback((socket: Socket) => {
    if (authRecoveryRef.current) return authRecoveryRef.current;

    const recovery = (async () => {
      try {
        const latestToken = localAuthRepository.getToken();
        const token =
          latestToken && latestToken !== handshakeTokenRef.current
            ? latestToken
            : await refreshStaffAccessToken();

        if (socketRef.current !== socket) return;

        handshakeTokenRef.current = token;
        socket.auth = { token: toBearerToken(token) };

        // AUTH_FAILED はサーバーがエラー通知の直後に切断する。通知内で
        // connect() だけを呼ぶと、その後の server disconnect に負けて
        // 再接続されないため、こちらから一度切断して新しい認証情報で張り直す。
        socket.disconnect();
        socket.connect();
      } catch (error) {
        console.error("Socket authentication recovery failed", error);
      }
    })().finally(() => {
      if (authRecoveryRef.current === recovery) {
        authRecoveryRef.current = null;
      }
    });

    authRecoveryRef.current = recovery;
    return recovery;
  }, []);

  useEffect(() => {
    const baseUrl = getPublicSocketBaseUrl();
    const token = localAuthRepository.getToken();

    if (!token) return;
    handshakeTokenRef.current = token;

    // Socket接続
    const socket = io(baseUrl, {
      transports: ["websocket"],
      auth: {
        token: toBearerToken(token), // バックエンドの認証仕様に合わせる
      },
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Socket connected");
      setIsConnected(true);
      joinedRoomIdsRef.current.forEach((roomId) => {
        emitJoinRoom(socket, roomId);
      });
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
      setIsConnected(false);
    });

    socket.on("connect_error", (error: Error) => {
      console.error("Socket connect error:", error.message);
      setIsConnected(false);
    });

    socket.on("chatError", (data: ChatSocketError | null) => {
      console.error("Chat error:", data);
      if (data?.code === "AUTH_FAILED") {
        void reconnectWithFreshToken(socket);
        return;
      }

      // ルーム配信時もサーバーはJWTを再検証する。JWT失効は権限エラーと
      // 同じコードで返るため、ルームごとに1度だけ再認証して再joinを試す。
      // 真の権限不足ならjoinedRoomイベントが来ず、再試行ループにはならない。
      if (
        data?.code === "ROOM_ACCESS_DENIED" &&
        data.roomId &&
        !roomAccessRecoveryAttemptsRef.current.has(data.roomId)
      ) {
        roomAccessRecoveryAttemptsRef.current.add(data.roomId);
        void reconnectWithFreshToken(socket);
      }
    });
    socket.on("newMessage", (message: ChatMessage | null) => {
      if (message?.chatRoomId) {
        roomAccessRecoveryAttemptsRef.current.delete(message.chatRoomId);
      }
    });
    socket.on("joinedRoom", (data: JoinedRoomEvent | null) => {
      if (data?.roomId) {
        roomAccessRecoveryAttemptsRef.current.delete(data.roomId);
      }
    });
    socket.io.on("reconnect_attempt", () => {
      const latestToken = localAuthRepository.getToken();
      if (latestToken) {
        handshakeTokenRef.current = latestToken;
        socket.auth = { token: toBearerToken(latestToken) };
      }
    });

    return () => {
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      authRecoveryRef.current = null;
    };
  }, [reconnectWithFreshToken]);

  const joinRoom = useCallback((roomId: string, _userId?: string) => {
    joinedRoomIdsRef.current.add(roomId);
    if (socketRef.current?.connected) {
      emitJoinRoom(socketRef.current, roomId);
    }
  }, []);

  const leaveRoom = useCallback((roomId: string) => {
    joinedRoomIdsRef.current.delete(roomId);
    roomAccessRecoveryAttemptsRef.current.delete(roomId);
    socketRef.current?.emit("leaveRoom", { roomId });
  }, []);

  const sendMessage = useCallback(
    (roomId: string, content: string, senderId: string) => {
      socketRef.current?.emit("sendMessage", {
        roomId,
        content,
        senderId,
      });
    },
    []
  );

  const onNewMessage = useCallback(
    (callback: (message: ChatMessage) => void) => {
      socketRef.current?.on("newMessage", callback);
      return () => {
        socketRef.current?.off("newMessage", callback);
      };
    },
    []
  );

  const onReactionAdded = useCallback(
    (callback: (reaction: ChatMessageReaction) => void) => {
      socketRef.current?.on("reactionAdded", callback);
      return () => {
        socketRef.current?.off("reactionAdded", callback);
      };
    },
    []
  );

  const onReactionRemoved = useCallback(
    (callback: (reaction: ChatMessageReaction) => void) => {
      socketRef.current?.on("reactionRemoved", callback);
      return () => {
        socketRef.current?.off("reactionRemoved", callback);
      };
    },
    []
  );

  const onRoomActivity = useCallback(
    (callback: (activity: ChatRoomActivity) => void) => {
      socketRef.current?.on("roomActivity", callback);
      return () => {
        socketRef.current?.off("roomActivity", callback);
      };
    },
    []
  );

  const onRoomWorkflowChanged = useCallback(
    (callback: (activity: ChatRoomWorkflowActivity) => void) => {
      socketRef.current?.on("roomWorkflowChanged", callback);
      return () => {
        socketRef.current?.off("roomWorkflowChanged", callback);
      };
    },
    []
  );

  const onRoomRead = useCallback(
    (callback: (event: ChatRoomReadEvent) => void) => {
      socketRef.current?.on("roomRead", callback);
      return () => {
        socketRef.current?.off("roomRead", callback);
      };
    },
    []
  );

  const onChatError = useCallback(
    (callback: (error: ChatSocketError) => void) => {
      socketRef.current?.on("chatError", callback);
      return () => {
        socketRef.current?.off("chatError", callback);
      };
    },
    []
  );

  const onStaffInboxChanged = useCallback(
    (callback: (event: StaffInboxChangedEvent) => void) => {
      socketRef.current?.on("staffInboxChanged", callback);
      return () => {
        socketRef.current?.off("staffInboxChanged", callback);
      };
    },
    []
  );

  return {
    socket: socketRef.current,
    isConnected,
    joinRoom,
    leaveRoom,
    sendMessage,
    onNewMessage,
    onReactionAdded,
    onReactionRemoved,
    onRoomActivity,
    onRoomWorkflowChanged,
    onRoomRead,
    onChatError,
    onStaffInboxChanged,
  };
};
